import { Options } from '@/components/Controls/Select';
import { TFunction } from 'i18next';
import { Dayjs } from 'dayjs';
import { IconPalette } from '@tabler/icons-react';
import {
  EntityFilterSelect,
  FilterDateRange,
  FilterEntity,
  FilterGrid,
  FilterSwitch,
} from './FilterKit';

type StructureFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllDistributors?: boolean;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  groupOptions?: Options[];
  distributorOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  displayGroupFilter?: boolean;
  displayDistributorFilter?: boolean;
  displayCompanyFilter?: boolean;
  displayBranchFilter?: boolean;
  startDate?: string;
  endDate?: string;
  isWhiteMark?: string;
  distributorId?: string;
  groupId?: string;
  companyId?: string;
  branchId?: string;
  searchBeginDate?: (value: Dayjs) => void;
  searchEndDate?: (value: Dayjs) => void;
  searchWhiteMark?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchByDistributor?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const StructureFilter = ({
  t,
  isLoadingAllDistributors,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  groupOptions,
  distributorOptions,
  companyOptions,
  branchOptions,
  displayGroupFilter,
  displayDistributorFilter,
  displayBranchFilter,
  displayCompanyFilter,
  startDate,
  endDate,
  isWhiteMark,
  distributorId,
  groupId,
  companyId,
  branchId,
  searchBeginDate,
  searchEndDate,
  searchWhiteMark,
  searchByDistributor,
  searchByGroup,
  searchByCompany,
  searchByBranch,
}: StructureFilterProps) => {
  return (
    <FilterGrid>
      <FilterDateRange
        label={t('common:createdBetween')}
        startDate={startDate}
        endDate={endDate}
        onStartChange={searchBeginDate}
        onEndChange={searchEndDate}
        className="sm:col-span-2"
      />
      {displayDistributorFilter && (
        <EntityFilterSelect
          entity={FilterEntity.DISTRIBUTOR}
          label={t('common:linkedToDistributor')}
          options={distributorOptions}
          value={distributorId}
          loading={isLoadingAllDistributors}
          emptyMessage={t('common:noDistributorAvailable')}
          onChange={searchByDistributor}
        />
      )}
      {displayGroupFilter && (
        <EntityFilterSelect
          entity={FilterEntity.GROUP}
          label={t('common:linkedToGroup')}
          options={groupOptions}
          value={groupId}
          loading={isLoadingAllGroups}
          emptyMessage={
            distributorId
              ? t('common:noGroupLinkedToThisDistributor')
              : undefined
          }
          onChange={searchByGroup}
        />
      )}
      {displayCompanyFilter && (
        <EntityFilterSelect
          entity={FilterEntity.COMPANY}
          label={t('common:linkedToCompany')}
          options={companyOptions}
          value={companyId}
          loading={isLoadingAllCompanies}
          emptyMessage={
            groupId
              ? t('common:noCompanyLinkedToThisGroup')
              : distributorId
                ? t('common:noCompanyLinkedToThisDistributor')
                : t('common:youAreNotLinkedToAnyCompany')
          }
          onChange={searchByCompany}
        />
      )}
      {displayBranchFilter && (
        <EntityFilterSelect
          entity={FilterEntity.BRANCH}
          label={t('common:linkedToBranch')}
          options={branchOptions}
          value={branchId}
          loading={isLoadingAllBranches}
          emptyMessage={
            companyId
              ? t('common:noBranchLinkedToThisCompany')
              : groupId
                ? t('common:noBranchLinkedToThisGroup')
                : distributorId
                  ? t('common:noBranchLinkedToThisDistributor')
                  : t('common:youAreNotLinkedToAnyBranch')
          }
          onChange={searchByBranch}
        />
      )}
      <FilterSwitch
        icon={IconPalette}
        tone="pink"
        label={t('common:onlyWhiteLabelCustomers')}
        checked={isWhiteMark === 'true'}
        onChange={searchWhiteMark}
      />
    </FilterGrid>
  );
};

export default StructureFilter;
